import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import {
  BaseButton,
  BaseCheckbox,
  BaseField,
  BaseInput,
  BaseSelect,
  BaseSegmentedControl,
} from '../base';
import { cn } from '../base/utils';
import type { WorkbenchGalleryColumn } from './workbenchGalleryUtils';

export interface WorkbenchGallerySettingsValue {
  visibleColumnKeys?: string[];
  galleryFieldKey?: string;
  galleryLayout?: 'fixed' | 'masonry' | string;
  galleryDensity?: 'compact' | 'comfortable' | 'detailed' | string;
  galleryGridColumns?: number | 'auto' | string;
  galleryBentoColumns?: 1 | 2 | 3 | number;
  galleryDetailVisibility?: 'all' | 'limit' | string;
  galleryDetailLimit?: number | string;
  [key: string]: any;
}

export interface WorkbenchGallerySettingsOption<Value extends string | number = string> {
  value: Value;
  label: ReactNode;
  disabled?: boolean;
}

export interface WorkbenchGallerySettingsLabels {
  settingsButton?: ReactNode;
  title?: ReactNode;
  visibleColumns?: ReactNode;
  coverField?: ReactNode;
  autoDetect?: ReactNode;
  cardDensity?: ReactNode;
  cardColumns?: ReactNode;
  detailColumns?: ReactNode;
  fieldVisibility?: ReactNode;
  fieldLimit?: ReactNode;
  fieldLimitHint?: ReactNode;
  layout?: ReactNode;
  compact?: ReactNode;
  comfortable?: ReactNode;
  detailed?: ReactNode;
  autoColumns?: ReactNode;
  columns?: (count: number) => ReactNode;
  bentoColumns?: (count: number) => ReactNode;
  topLimit?: ReactNode;
  allFields?: ReactNode;
  adaptiveGrid?: ReactNode;
  masonry?: ReactNode;
}

export interface WorkbenchGallerySettingsPanelClassNames {
  root?: string;
  section?: string;
  sectionHeader?: string;
  columnList?: string;
  columnItem?: string;
  control?: string;
}

export interface WorkbenchGallerySettingsPanelProps {
  columns?: WorkbenchGalleryColumn[];
  galleryFieldOptions?: WorkbenchGalleryColumn[];
  value?: WorkbenchGallerySettingsValue;
  onValueChange?: (
    nextValue: WorkbenchGallerySettingsValue,
    patch: Partial<WorkbenchGallerySettingsValue>,
  ) => void;
  labels?: WorkbenchGallerySettingsLabels;
  densityOptions?: Array<WorkbenchGallerySettingsOption>;
  gridColumnOptions?: Array<WorkbenchGallerySettingsOption<number | 'auto'>>;
  bentoColumnOptions?: Array<WorkbenchGallerySettingsOption<number>>;
  detailVisibilityOptions?: Array<WorkbenchGallerySettingsOption>;
  layoutOptions?: Array<WorkbenchGallerySettingsOption>;
  getColumnLabel?: (column: WorkbenchGalleryColumn) => ReactNode;
  getColumnDescription?: (column: WorkbenchGalleryColumn) => ReactNode;
  showColumnVisibility?: boolean;
  showGalleryControls?: boolean;
  showLayoutControl?: boolean;
  showGridColumns?: boolean;
  showDetailColumns?: boolean;
  allowEmptyVisibleColumns?: boolean;
  className?: string;
  classNames?: WorkbenchGallerySettingsPanelClassNames;
}

export interface WorkbenchGallerySettingsButtonClassNames {
  root?: string;
  trigger?: string;
  popover?: string;
  header?: string;
  panel?: string;
}

export interface WorkbenchGallerySettingsButtonProps extends WorkbenchGallerySettingsPanelProps {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  triggerIcon?: ReactNode;
  triggerLabel?: ReactNode;
  triggerTitle?: string;
  align?: 'start' | 'end';
  popoverWidth?: number;
  panelHeader?: ReactNode;
  panelBefore?: ReactNode;
  panelAfter?: ReactNode;
  panelClassName?: string;
  buttonClassName?: string;
  popoverClassName?: string;
  buttonClassNames?: WorkbenchGallerySettingsButtonClassNames;
}

const defaultLabels: Required<WorkbenchGallerySettingsLabels> = {
  settingsButton: 'Gallery settings',
  title: 'Gallery settings',
  visibleColumns: 'Visible fields',
  coverField: 'Cover field',
  autoDetect: 'Auto',
  cardDensity: 'Card density',
  cardColumns: 'Cards per row',
  detailColumns: 'Detail columns',
  fieldVisibility: 'Field visibility',
  fieldLimit: 'Field limit',
  fieldLimitHint: 'Uses current field order',
  layout: 'Gallery layout',
  compact: 'Compact',
  comfortable: 'Standard',
  detailed: 'Detailed',
  autoColumns: 'Auto',
  columns: (count) => `${count}`,
  bentoColumns: (count) => `${count}`,
  topLimit: 'First fields',
  allFields: 'All fields',
  adaptiveGrid: 'Grid',
  masonry: 'Masonry',
};

function normalizeColumnKey(column: WorkbenchGalleryColumn | undefined) {
  return String(column?.key || '');
}

function getDefaultColumnLabel(column: WorkbenchGalleryColumn) {
  return column.label ?? column.key;
}

function getDefaultVisibleColumnKeys(columns: WorkbenchGalleryColumn[]) {
  return columns
    .filter((column) => normalizeColumnKey(column) && column.hidden !== true)
    .map((column) => normalizeColumnKey(column));
}

function getResolvedVisibleColumnKeys({
  columns,
  visibleColumnKeys,
  allowEmptyVisibleColumns,
}: {
  columns: WorkbenchGalleryColumn[];
  visibleColumnKeys?: string[];
  allowEmptyVisibleColumns?: boolean;
}) {
  const validKeys = new Set(columns.map(normalizeColumnKey).filter(Boolean));
  const controlledKeys = Array.isArray(visibleColumnKeys)
    ? visibleColumnKeys.filter((key) => validKeys.has(String(key)))
    : null;

  if (controlledKeys && (controlledKeys.length || allowEmptyVisibleColumns)) {
    return controlledKeys;
  }

  return getDefaultVisibleColumnKeys(columns);
}

function getDefaultDensityOptions(
  labels: Required<WorkbenchGallerySettingsLabels>,
): Array<WorkbenchGallerySettingsOption> {
  return [
    { value: 'compact', label: labels.compact },
    { value: 'comfortable', label: labels.comfortable },
    { value: 'detailed', label: labels.detailed },
  ];
}

function getDefaultGridColumnOptions(
  labels: Required<WorkbenchGallerySettingsLabels>,
): Array<WorkbenchGallerySettingsOption<number | 'auto'>> {
  return [
    { value: 'auto' as const, label: labels.autoColumns },
    { value: 1, label: labels.columns(1) },
    { value: 2, label: labels.columns(2) },
    { value: 3, label: labels.columns(3) },
    { value: 4, label: labels.columns(4) },
  ];
}

function getDefaultBentoColumnOptions(
  labels: Required<WorkbenchGallerySettingsLabels>,
): Array<WorkbenchGallerySettingsOption<number>> {
  return [
    { value: 1, label: labels.bentoColumns(1) },
    { value: 2, label: labels.bentoColumns(2) },
    { value: 3, label: labels.bentoColumns(3) },
  ];
}

function getDefaultDetailVisibilityOptions(
  labels: Required<WorkbenchGallerySettingsLabels>,
): Array<WorkbenchGallerySettingsOption> {
  return [
    { value: 'limit', label: labels.topLimit },
    { value: 'all', label: labels.allFields },
  ];
}

function getDefaultLayoutOptions(
  labels: Required<WorkbenchGallerySettingsLabels>,
): Array<WorkbenchGallerySettingsOption> {
  return [
    { value: 'fixed', label: labels.adaptiveGrid },
    { value: 'masonry', label: labels.masonry },
  ];
}

function normalizeOptionValue(value: string | number) {
  return String(value);
}

function getOptionValue<Value extends string | number>(
  options: Array<WorkbenchGallerySettingsOption<Value>>,
  rawValue: unknown,
  fallbackValue: Value,
) {
  const valueText = String(rawValue ?? fallbackValue);
  return options.some((option) => String(option.value) === valueText)
    ? valueText
    : String(fallbackValue);
}

function parseNumberOption(value: string) {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : 1;
}

const compactSegmentedClassNames = {
  root: 'flex w-full flex-wrap',
  item: 'min-w-[58px] flex-1 px-2 text-[11px]',
  label: 'text-[11px]',
};

export function WorkbenchGallerySettingsPanel({
  columns = [],
  galleryFieldOptions,
  value,
  onValueChange,
  labels,
  densityOptions,
  gridColumnOptions,
  bentoColumnOptions,
  detailVisibilityOptions,
  layoutOptions,
  getColumnLabel = getDefaultColumnLabel,
  getColumnDescription,
  showColumnVisibility = true,
  showGalleryControls = true,
  showLayoutControl = true,
  showGridColumns = true,
  showDetailColumns = true,
  allowEmptyVisibleColumns = false,
  className,
  classNames,
}: WorkbenchGallerySettingsPanelProps) {
  const resolvedLabels = { ...defaultLabels, ...labels };
  const resolvedValue = value || {};
  const resolvedColumns = columns.filter((column) => normalizeColumnKey(column));
  const resolvedVisibleColumnKeys = getResolvedVisibleColumnKeys({
    columns: resolvedColumns,
    visibleColumnKeys: resolvedValue.visibleColumnKeys,
    allowEmptyVisibleColumns,
  });
  const visibleColumnKeySet = new Set(resolvedVisibleColumnKeys);
  const resolvedGalleryFieldOptions = (galleryFieldOptions || resolvedColumns)
    .filter((column) => normalizeColumnKey(column));
  const resolvedDensityOptions = densityOptions || getDefaultDensityOptions(resolvedLabels);
  const resolvedGridColumnOptions = gridColumnOptions || getDefaultGridColumnOptions(resolvedLabels);
  const resolvedBentoColumnOptions = bentoColumnOptions || getDefaultBentoColumnOptions(resolvedLabels);
  const resolvedDetailVisibilityOptions = detailVisibilityOptions || getDefaultDetailVisibilityOptions(resolvedLabels);
  const resolvedLayoutOptions = layoutOptions || getDefaultLayoutOptions(resolvedLabels);
  const canUpdate = typeof onValueChange === 'function';

  const updateValue = (patch: Partial<WorkbenchGallerySettingsValue>) => {
    onValueChange?.({ ...resolvedValue, ...patch }, patch);
  };

  const toggleColumn = (columnKey: string) => {
    if (!canUpdate) return;
    const nextVisibleSet = new Set(resolvedVisibleColumnKeys);
    if (nextVisibleSet.has(columnKey)) {
      nextVisibleSet.delete(columnKey);
    } else {
      nextVisibleSet.add(columnKey);
    }

    let nextVisibleColumnKeys = resolvedColumns
      .map(normalizeColumnKey)
      .filter((key) => nextVisibleSet.has(key));

    if (!nextVisibleColumnKeys.length && !allowEmptyVisibleColumns) {
      nextVisibleColumnKeys = [columnKey];
    }

    updateValue({ visibleColumnKeys: nextVisibleColumnKeys });
  };

  const shouldRenderColumnVisibility = showColumnVisibility && resolvedColumns.length > 0;
  const shouldRenderGalleryControls = showGalleryControls;

  return (
    <div className={cn('space-y-3 text-foreground', classNames?.root, className)}>
      {shouldRenderColumnVisibility ? (
        <section className={cn('space-y-2', classNames?.section)}>
          <div className={cn('text-[10px] font-medium uppercase tracking-wide text-muted-foreground', classNames?.sectionHeader)}>
            {resolvedLabels.visibleColumns}
          </div>
          <div className={cn('max-h-[220px] space-y-1 overflow-y-auto pr-1', classNames?.columnList)}>
            {resolvedColumns.map((column) => {
              const columnKey = normalizeColumnKey(column);
              return (
                <BaseCheckbox
                  key={`workbench-gallery-column-${columnKey}`}
                  checked={visibleColumnKeySet.has(columnKey)}
                  disabled={!canUpdate}
                  label={getColumnLabel(column)}
                  description={getColumnDescription?.(column)}
                  onChange={() => toggleColumn(columnKey)}
                  className={cn('flex w-full rounded-lg px-2 py-1.5 hover:bg-muted/50', classNames?.columnItem)}
                />
              );
            })}
          </div>
        </section>
      ) : null}

      {shouldRenderGalleryControls ? (
        <section className={cn('space-y-3 border-t border-border/60 pt-3', classNames?.section)}>
          <BaseField
            label={resolvedLabels.coverField}
            classNames={{
              label: 'text-[10px] uppercase tracking-wide text-muted-foreground',
            }}
          >
            <BaseSelect
              value={String(resolvedValue.galleryFieldKey || 'auto')}
              options={[
                { value: 'auto', label: resolvedLabels.autoDetect },
                ...resolvedGalleryFieldOptions.map((column) => ({
                  value: normalizeColumnKey(column),
                  label: getColumnLabel(column),
                })),
              ]}
              disabled={!canUpdate}
              onChange={(event) => updateValue({ galleryFieldKey: event.currentTarget.value || 'auto' })}
              className={classNames?.control}
            />
          </BaseField>

          <BaseField
            label={resolvedLabels.cardDensity}
            classNames={{
              label: 'text-[10px] uppercase tracking-wide text-muted-foreground',
            }}
          >
            <BaseSegmentedControl
              value={getOptionValue(resolvedDensityOptions, resolvedValue.galleryDensity, 'comfortable')}
              options={resolvedDensityOptions.map((option) => ({
                value: normalizeOptionValue(option.value),
                label: option.label,
                disabled: option.disabled,
              }))}
              onValueChange={(nextValue) => updateValue({ galleryDensity: nextValue })}
              className={cn('w-full', classNames?.control)}
              classNames={compactSegmentedClassNames}
            />
          </BaseField>

          {showGridColumns ? (
            <BaseField
              label={resolvedLabels.cardColumns}
              classNames={{
                label: 'text-[10px] uppercase tracking-wide text-muted-foreground',
              }}
            >
              <BaseSegmentedControl
                value={getOptionValue(resolvedGridColumnOptions, resolvedValue.galleryGridColumns, 'auto')}
                options={resolvedGridColumnOptions.map((option) => ({
                  value: normalizeOptionValue(option.value),
                  label: option.label,
                  disabled: option.disabled,
                }))}
                onValueChange={(nextValue) => updateValue({
                  galleryGridColumns: nextValue === 'auto' ? 'auto' : parseNumberOption(nextValue),
                })}
                className={cn('w-full', classNames?.control)}
                classNames={compactSegmentedClassNames}
              />
            </BaseField>
          ) : null}

          {showDetailColumns ? (
            <BaseField
              label={resolvedLabels.detailColumns}
              classNames={{
                label: 'text-[10px] uppercase tracking-wide text-muted-foreground',
              }}
            >
              <BaseSegmentedControl
                value={getOptionValue(resolvedBentoColumnOptions, resolvedValue.galleryBentoColumns, 2)}
                options={resolvedBentoColumnOptions.map((option) => ({
                  value: normalizeOptionValue(option.value),
                  label: option.label,
                  disabled: option.disabled,
                }))}
                onValueChange={(nextValue) => updateValue({ galleryBentoColumns: parseNumberOption(nextValue) })}
                className={cn('w-full', classNames?.control)}
                classNames={compactSegmentedClassNames}
              />
            </BaseField>
          ) : null}

          <BaseField
            label={resolvedLabels.fieldVisibility}
            classNames={{
              label: 'text-[10px] uppercase tracking-wide text-muted-foreground',
            }}
          >
            <BaseSegmentedControl
              value={getOptionValue(resolvedDetailVisibilityOptions, resolvedValue.galleryDetailVisibility, 'limit')}
              options={resolvedDetailVisibilityOptions.map((option) => ({
                value: normalizeOptionValue(option.value),
                label: option.label,
                disabled: option.disabled,
              }))}
              onValueChange={(nextValue) => updateValue({ galleryDetailVisibility: nextValue })}
              className={cn('w-full', classNames?.control)}
              classNames={compactSegmentedClassNames}
            />
          </BaseField>

          {resolvedValue.galleryDetailVisibility !== 'all' ? (
            <BaseField
              label={resolvedLabels.fieldLimit}
              description={resolvedLabels.fieldLimitHint}
              classNames={{
                label: 'text-[10px] uppercase tracking-wide text-muted-foreground',
                description: 'text-[10px]',
              }}
            >
              <BaseInput
                type="number"
                min={1}
                step={1}
                value={String(resolvedValue.galleryDetailLimit || 4)}
                disabled={!canUpdate}
                onChange={(event) => updateValue({ galleryDetailLimit: event.currentTarget.value })}
                className={classNames?.control}
              />
            </BaseField>
          ) : null}

          {showLayoutControl ? (
            <BaseField
              label={resolvedLabels.layout}
              classNames={{
                label: 'text-[10px] uppercase tracking-wide text-muted-foreground',
              }}
            >
              <BaseSegmentedControl
                value={getOptionValue(resolvedLayoutOptions, resolvedValue.galleryLayout, 'fixed')}
                options={resolvedLayoutOptions.map((option) => ({
                  value: normalizeOptionValue(option.value),
                  label: option.label,
                  disabled: option.disabled,
                }))}
                onValueChange={(nextValue) => updateValue({ galleryLayout: nextValue })}
                className={cn('w-full', classNames?.control)}
                classNames={compactSegmentedClassNames}
              />
            </BaseField>
          ) : null}
        </section>
      ) : null}
    </div>
  );
}

export function WorkbenchGallerySettingsButton({
  open,
  defaultOpen = false,
  onOpenChange,
  triggerIcon,
  triggerLabel,
  triggerTitle,
  align = 'end',
  popoverWidth = 320,
  panelHeader,
  panelBefore,
  panelAfter,
  panelClassName,
  buttonClassName,
  popoverClassName,
  buttonClassNames,
  labels,
  className,
  classNames,
  ...panelProps
}: WorkbenchGallerySettingsButtonProps) {
  const resolvedLabels = { ...defaultLabels, ...labels };
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const [popoverStyle, setPopoverStyle] = useState<{
    left?: number;
    top?: number;
    width?: number;
    maxHeight?: string;
  }>({});
  const isControlled = open !== undefined;
  const isOpen = isControlled ? Boolean(open) : uncontrolledOpen;

  const setOpen = (nextOpen: boolean) => {
    if (!isControlled) {
      setUncontrolledOpen(nextOpen);
    }
    onOpenChange?.(nextOpen);
  };

  useLayoutEffect(() => {
    if (!isOpen) return undefined;

    const updatePopoverPosition = () => {
      const triggerRect = rootRef.current?.getBoundingClientRect();
      if (!triggerRect || typeof window === 'undefined') return;

      const viewportPadding = 12;
      const availableWidth = Math.max(window.innerWidth - viewportPadding * 2, 0);
      const resolvedWidth = Math.min(popoverWidth, availableWidth || popoverWidth);
      const preferredLeft = align === 'start'
        ? triggerRect.left
        : triggerRect.right - resolvedWidth;
      const left = Math.min(
        Math.max(preferredLeft, viewportPadding),
        Math.max(window.innerWidth - resolvedWidth - viewportPadding, viewportPadding),
      );
      const top = Math.min(
        triggerRect.bottom + 8,
        Math.max(window.innerHeight - 80, viewportPadding),
      );

      setPopoverStyle({
        left,
        top,
        width: resolvedWidth,
        maxHeight: `calc(100vh - ${Math.round(top + viewportPadding)}px)`,
      });
    };
    const handlePointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    updatePopoverPosition();
    window.addEventListener('resize', updatePopoverPosition);
    window.addEventListener('scroll', updatePopoverPosition, true);
    window.addEventListener('pointerdown', handlePointerDown);
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('resize', updatePopoverPosition);
      window.removeEventListener('scroll', updatePopoverPosition, true);
      window.removeEventListener('pointerdown', handlePointerDown);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [align, isOpen, popoverWidth]);

  const resolvedTriggerLabel = triggerLabel ?? resolvedLabels.settingsButton;
  const resolvedPanelHeader = panelHeader ?? resolvedLabels.title;

  return (
    <div ref={rootRef} className={cn('relative inline-flex', buttonClassNames?.root, className)}>
      <BaseButton
        icon={triggerIcon}
        label={resolvedTriggerLabel}
        title={triggerTitle || (typeof resolvedTriggerLabel === 'string' ? resolvedTriggerLabel : undefined)}
        iconOnly={Boolean(triggerIcon)}
        active={isOpen}
        tone="muted"
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        className={cn(buttonClassNames?.trigger, buttonClassName)}
        onClick={() => setOpen(!isOpen)}
      />

      {isOpen ? (
        <div
          role="dialog"
          aria-label={typeof resolvedPanelHeader === 'string' ? resolvedPanelHeader : undefined}
          className={cn(
            'fixed z-50 overflow-y-auto rounded-xl border border-border bg-card p-3 text-foreground shadow-lg',
            align === 'start' ? 'origin-top-left' : 'origin-top-right',
            buttonClassNames?.popover,
            popoverClassName,
          )}
          style={popoverStyle}
        >
          {resolvedPanelHeader ? (
            <div className={cn('mb-3 text-[11px] font-medium text-foreground', buttonClassNames?.header)}>
              {resolvedPanelHeader}
            </div>
          ) : null}
          {panelBefore}
          <WorkbenchGallerySettingsPanel
            {...panelProps}
            labels={resolvedLabels}
            className={cn(buttonClassNames?.panel, panelClassName)}
            classNames={classNames}
          />
          {panelAfter}
        </div>
      ) : null}
    </div>
  );
}
